import { encryptLoginPassword } from '@tiger/api-signature';
import { Button, Form, Input } from 'antd';
import { observer } from 'mobx-react';
import React, { createRef, FC, Fragment, ReactNode, useLayoutEffect, useRef, useState } from 'react';
import { $Formutil, FormItem, withForm } from 'react-antd-formutil';
import { Helmet } from 'react-helmet';
import { Link, useSearchParams } from 'react-router-dom';
import GeeCaptcha, { CaptchaType } from 'components/Captcha';
import Debug from 'components/Debug';
import Email from 'components/forms/Email';
import TelPhone from 'components/forms/TelPhone';
import { openNativeModal } from 'components/Modal';
import Toast, { Loading } from 'components/Toast';
import VerifyCodeSend from 'components/VerifyCodeSend';
import logo from 'static/images/logo/logo.png';
import security from 'stores/security';
import patterns from 'utils/patterns';
import require2FA from 'utils/require2FA';
import URL from 'utils/URL';
import SafeVerifyForm, { SafeVerifyData } from './SafeVerify';
import './style.scss';

export interface LoginProps {
    $formutil: $Formutil<any>;
}

export interface LoginState {
    captcha: boolean;
    initialLogged: boolean; // 初始的登录态状况
}

const showFieldError = (fieldUtil, errMsg) => {
    if (!fieldUtil) {
        Toast.show(errMsg);
    } else {
        fieldUtil.$setError({ isError: errMsg });
    }
};

const formatNextUrl = (next: string) => {
    if (__DEV__ && next.startsWith('http://localhost')) {
        return URL.merge(next, {
            query: {
                token: security.ACCESS_TOKEN
            }
        });
    }

    return next;
};

type LoginType = 'email' | 'phone' | 'phoneCode' | 'emailCode';

const Login: FC<LoginProps> = observer(({ $formutil }) => {
    const [searchParams] = useSearchParams();
    const [captcha, setCaptcha] = useState(false);
    const [initialLogged] = useState(security.isLogin);
    const source = searchParams.get('source');
    const loginTypeParam = (searchParams.get('loginType') || 'email') as LoginType;
    // 默认邮箱登录方式
    const loginType = ['email', 'phone', 'phoneCode', 'emailCode'].includes(loginTypeParam) ? loginTypeParam : 'email';
    const nextUrl = searchParams.get('next');
    const safeVerifyModal = useRef<ReturnType<typeof openNativeModal>>();

    useLayoutEffect(() => {
        if (security.isLogin) {
            if (nextUrl && patterns.isSafeUrl(nextUrl)) {
                window.location.href = formatNextUrl(nextUrl);
            } else {
                window.location.replace('/profile');
            }
        }

        // eslint-disable-next-line
    }, []);

    const captchaRef = createRef<any>();

    const onSubmit = async (ev?: React.FormEvent) => {
        ev?.preventDefault?.();
        login();
    };

    const login = async (extraParams = {}, safeVerifyData?: SafeVerifyData) => {
        const { $invalid, $errors, $params, $batchState } = $formutil;

        if ($invalid) {
            $batchState({
                $dirty: true,
                $touched: true
            });

            if ($errors.validate_code) {
                captchaRef.current!.trigger();
            }
        } else {
            Toast.loading(true);

            try {
                await security.login({
                    ...$params,
                    ...extraParams,
                    grant_type: loginType,
                    ...(!safeVerifyData && $params.login_password
                        ? {
                              pwd_api_ver: 'v2',
                              login_password: encryptLoginPassword($params.login_password)
                          }
                        : {}),
                    ...safeVerifyData
                });

                // 带有next，则直接跳转到next
                if (nextUrl && patterns.isSafeUrl(nextUrl)) {
                    window.location.href = formatNextUrl(nextUrl);
                } else {
                    // 登录页面是不内嵌的，除了app内，其他情况下 /profile 都是内嵌的，所以这里用window.location.replace跳转
                    window.location.replace('/profile');
                }
            } catch (error) {
                const error_code = error.error_code;
                let showToast = true;

                // 安全需求：需要通过手机验证码方式再次验证。
                // 11001是需要手机号验证，11002是需要邮箱验证
                if (error_code === 11001 || error_code === 11002) {
                    showToast = false;

                    safeVerifyModal.current = openNativeModal({
                        component: modalProps => (
                            <SafeVerifyForm
                                {...modalProps}
                                type={error_code === 11002 ? 'email' : 'phone'}
                                username={$params.username}
                                tel_code={$params.tel_code}
                                onSubmit={async data => {
                                    await login(undefined, data);
                                }}
                            />
                        ),
                        width: 380,
                        noStickyBottom: true,
                        centered: true
                    });

                    safeVerifyModal.current.result.catch(() => {});
                }

                const needCaptcha = captcha || (error_code >= 11011 && error_code <= 11015); // 验证码为空

                // 11401表示动态密码(TradeUP Authenticator)不能为空
                if (error_code === 11401 || error_code === 11402) {
                    showToast = false;

                    if (error_code !== 11401) {
                        handleError(error);
                    }

                    try {
                        Toast.loading(false);

                        const otp_code = await require2FA();

                        login(
                            {
                                otp_code
                            },
                            safeVerifyData
                        );

                        safeVerifyModal.current?.close();
                        safeVerifyModal.current = undefined;
                    } catch (error) {
                        Toast.show(error);
                    }
                } else {
                    if (needCaptcha) {
                        showToast = false;
                        setCaptcha(needCaptcha);
                    }

                    if (showToast) {
                        handleError(error);
                    }

                    // 如果验证码框已经出现，就刷新验证码
                    // 这里统一处理成只要请求出错，就刷新一次验证码
                    if (captchaRef.current) {
                        captchaRef.current.reset();

                        if (captchaRef.current.captchaType === CaptchaType.RECAPTCHA_SCORE) {
                            setCaptcha(false);
                        }
                    }

                    Toast.loading(false);
                }
            }
        }
    };

    // 根据不同的error_code显示不同的错误提示信息
    const handleError = error => {
        const usernameFieldUtil = $formutil.$getField('username');
        const passwordFieldUtil = $formutil.$getField('login_password');
        const isPhone = loginType === 'phone';
        let errMsg = error.error_msg;

        switch (Number(error.error_code)) {
            case 10317:
                errMsg = __('Your account has been locked due to multiple failed login attempts');
                showFieldError(usernameFieldUtil, errMsg);
                break;
            case 11054:
                errMsg = __(
                    'Your account has been locked due to multiple failed login attempts. Please try again in 3 hours'
                );

                showFieldError(passwordFieldUtil, errMsg);

                break;
            case 11055:
                errMsg = __('Wrong user name or password');
                showFieldError(passwordFieldUtil, errMsg);

                break;
            case 11064:
                if (!isPhone) {
                    errMsg = __('Your e-mail is not verified');
                    showFieldError(usernameFieldUtil, errMsg);
                }

                break;
            case 11402:
            case 11406:
                errMsg =
                    Number(error.error_code) === 11402
                        ? __('Invalid code. Please check your code and try again')
                        : __('Too many failed attempts');

                Toast.show(errMsg);
                break;
            default:
                Toast.show(errMsg);
                break;
        }
    };

    const latestQuery = URL.current().query;
    const isEsop = source === 'esop';

    const renderOtherLogin = () => {
        const loginTypeOtherMap: Record<LoginType, Array<{ type: LoginType; text: string }>> = {
            email: [
                { type: 'emailCode', text: __('Login with Email verification code') },
                { type: 'phone', text: __('Login with Phone') }
            ],
            phone: [
                { type: 'phoneCode', text: __('Login with verification code') },
                { type: 'email', text: __('Login with Email') }
            ],
            phoneCode: [
                { type: 'email', text: __('Login with Email') },
                { type: 'phone', text: __('Login with password') }
            ],
            emailCode: [
                { type: 'phone', text: __('Login with phone') },
                { type: 'email', text: __('Login with password') }
            ]
        };

        return (
            <div className="other-login-type">
                {loginTypeOtherMap[loginType].reduce((prev, { type: curLoginType, text }) => {
                    prev.push(
                        <Fragment key={curLoginType}>
                            {prev.length > 0 && <span className="mx-1">|</span>}
                            <Link
                                to={{
                                    pathname: '/login',
                                    search: URL.merge('', { query: { ...latestQuery, loginType: curLoginType } })
                                }}>
                                {text}
                            </Link>
                        </Fragment>
                    );

                    return prev;
                }, [] as ReactNode[])}
            </div>
        );
    };

    // 初始已登录，则直接展示loading，此时浏览器处于跳转状态
    if (initialLogged) {
        return (
            <div className="page-login-root">
                <Loading tip={__('登录中')} />
            </div>
        );
    }

    return (
        <Fragment>
            <Helmet>
                <title>Log In | Online Trading Platform - TradeUP</title>
                <meta name="description" content="Log into your TradeUP trading account!" />
            </Helmet>
            <Form noValidate className="page-login-root form-required-hidden" layout="vertical" onFinish={onSubmit}>
                <header className="header">
                    <a href="/" title="tradeup">
                        <img className="logo" src={logo} alt="logo" />
                    </a>
                </header>
                {['phone', 'phoneCode'].includes(loginType) || isEsop ? (
                    <TelPhone phoneName="username" label={__('Phone Number')} defaultTelcode={1} line={false} />
                ) : (
                    <Email label={__('Email Address')} name="username" line={false} />
                )}
                {['phoneCode', 'emailCode'].includes(loginType) ? (
                    <VerifyCodeSend
                        label={loginType === 'phoneCode' ? __('SMS verification code') : __('Email verification code')}
                        username={$formutil.$params.username || ''}
                        name="verify_code"
                        process="verify_code_signin"
                        type={loginType === 'phoneCode' ? 'phone' : 'email'}
                        tel_code={$formutil.$params.tel_code}
                        line={false}
                    />
                ) : (
                    <FormItem
                        name="login_password"
                        required
                        isError
                        $validators={{ isError: () => true }}
                        validMessage={{
                            required: __('Please enter your password.')
                        }}
                        className="password-form-item"
                        label={
                            <div className="w-100 d-flex justify-content-between align-items-center">
                                <span>{__('Password')}</span>
                                <a
                                    href={URL.merge('/account/password/verify', {
                                        query: { ...latestQuery, loginType: 'email' }
                                    })}>
                                    {__('Forget your password?')}
                                </a>
                            </div>
                        }>
                        <Input type="password" placeholder={__('Password')} />
                    </FormItem>
                )}

                {captcha && (
                    <div className="form-group">
                        <GeeCaptcha action="signin" ref={captchaRef} onSuccess={() => onSubmit()} />
                    </div>
                )}

                <Button block type="primary" size="large" htmlType="submit">
                    {__('Login')}
                </Button>

                {!isEsop && (
                    <>
                        {renderOtherLogin()}
                        <footer className="footer">
                            <hr />
                            <a
                                href={URL.merge(window.location.href, { pathname: '/accounts' })}
                                className="text-center">
                                {__('Sign up with email or phone number')}
                            </a>
                        </footer>
                    </>
                )}
                <Debug data={$formutil.$params} />
            </Form>
        </Fragment>
    );
});

export default withForm(Login);
